"use client"

import { useEffect, useRef, type ReactNode } from "react"
import Lenis from "lenis"

interface SmoothScrollProviderProps {
    children: ReactNode
}

export function SmoothScrollProvider({ children }: SmoothScrollProviderProps) {
    const lenisRef = useRef<Lenis | null>(null)

    useEffect(() => {
        // Respect user's motion preferences — skip Lenis entirely
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (prefersReducedMotion) return

        const isMobile = window.innerWidth < 768

        // Mobile-optimized config: lower lerp = less computation per frame,
        // shorter duration = snappier response, lower touchMultiplier = less overshoot
        lenisRef.current = new Lenis({
            duration: isMobile ? 0.6 : 0.8,
            easing: (t) => 1 - Math.pow(1 - t, 3),
            orientation: "vertical",
            gestureOrientation: "vertical",
            smoothWheel: true,
            lerp: isMobile ? 0.15 : 0.1,
            wheelMultiplier: isMobile ? 1.0 : 1.2,
            touchMultiplier: isMobile ? 1.2 : 1.8,
            infinite: false,
            autoResize: true,
        })

        let rafId: number
        function raf(time: number) {
            lenisRef.current?.raf(time)
            rafId = requestAnimationFrame(raf)
        }

        rafId = requestAnimationFrame(raf)

        // @ts-expect-error - Adding lenis to window for global access
        window.lenis = lenisRef.current

        return () => {
            cancelAnimationFrame(rafId)
            lenisRef.current?.destroy()
            // @ts-expect-error - Cleaning up global reference
            window.lenis = undefined
        }
    }, [])

    return <>{children}</>
}
